import { useForm } from "react-hook-form";
import type { Error as LoginError } from "@auth0/auth0-acul-js";

import { butterfieldLogo } from "@/assets/logos";
import { useLoginPasswordManager } from "../hooks/useLoginPasswordManager";
import { getFieldError } from "../utils/helpers/errorUtils";
import { rebaseLinkToCurrentOrigin } from "../utils/helpers/urlUtils";

// const heroImageUrl =
//   "https://media.butterfield.com/page-image_imageUrl_a470c5db-2106-4964-a8f2-414c0415efe6_1744290400564.jpg";
const heroImageUrl =
  "https://cdn.mediavalet.com/cato/butterfield/aEeF6_P6lk6o6VqdMg_3cQ/GX_bog2pTkawcjy_quukQA/Large/DSC09355-slovenia-ivan-sardi-2026.jpeg?f=auto";

interface LoginPasswordFormData {
  email: string;
  password: string;
  captcha?: string;
}

function LoginPasswordScreen() {
  const {
    handleLoginPassword,
    errors,
    texts,
    isForgotPasswordEnabled,
    isSignupEnabled,
    signupLink,
    resetPasswordLink,
    isCaptchaAvailable,
    captchaImage,
    editIdentifierLink,
    prefilledIdentifier,
  } = useLoginPasswordManager();

  const form = useForm<LoginPasswordFormData>({
    defaultValues: {
      email: prefilledIdentifier,
      password: "",
      captcha: "",
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors: formErrors, isSubmitting },
  } = form;

  const sdkErrors = (errors || []) as LoginError[];
  const generalErrors = sdkErrors.filter((err) => !err.field);
  const emailSDKError =
    getFieldError("username", sdkErrors) ||
    getFieldError("email", sdkErrors) ||
    getFieldError("identifier", sdkErrors);
  const passwordSDKError = getFieldError("password", sdkErrors);
  const captchaSDKError = getFieldError("captcha", sdkErrors);

  const emailLabel = (texts?.emailPlaceholder ||
    texts?.usernameOrEmailPlaceholder ||
    "Email Address"
  ).concat("*");
  const emailPlaceholder =
    texts?.identifierPlaceholder ||
    texts?.emailPlaceholder ||
    "you@company.com";
  const passwordLabel = (texts?.passwordPlaceholder || "Password").concat("*");
  const captchaLabel = (texts?.captchaCodePlaceholder || "CAPTCHA").concat("*");
  const buttonText = texts?.buttonText || "Continue";
  const forgotPasswordText = texts?.forgotPasswordText || "Forgot password?";
  const signupActionText =
    texts?.signupActionText || texts?.footerText || "Don't have an account?";
  const signupLinkText =
    texts?.signupActionLinkText || texts?.footerLinkText || "Sign up";
  const localizedSignupLink =
    signupLink && rebaseLinkToCurrentOrigin(signupLink);
  const localizedResetLink =
    resetPasswordLink && rebaseLinkToCurrentOrigin(resetPasswordLink);
  const localizedEditIdentifierLink =
    editIdentifierLink && rebaseLinkToCurrentOrigin(editIdentifierLink);

  const onSubmit = async (data: LoginPasswordFormData) => {
    await handleLoginPassword(data.email, data.password, data.captcha);
  };

  return (
    <div className="overflow-hidden rounded-none bg-white shadow-[0_30px_80px_rgba(2,6,23,0.35)] min-h-screen">
      <div className="grid min-h-screen grid-cols-1 md:grid-cols-2">
        <div className="relative order-1 h-56 sm:h-72 md:order-2 md:h-full">
          <img
            src={heroImageUrl}
            alt="Auth0 experience preview"
            className="absolute inset-0 h-full w-full object-cover"
            loading="lazy"
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-slate-900/10 via-transparent to-slate-900/30" />
        </div>

        <form
          className="flex h-full flex-col justify-center gap-6 order-2 bg-white px-10 py-12 md:order-1 md:px-12"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="mb-0 flex flex-col items-start">
            <img
              src={butterfieldLogo}
              alt="Butterfield logo"
              className="h-14 w-auto"
              loading="lazy"
            />
            <p className="mt-4 font-display text-xl font-normal text-ink">
              {texts?.title || "Enter your password to continue"}
            </p>
          </div>

          {generalErrors.length > 0 && (
            <div className="rounded-none border border-danger/30 bg-danger-bg px-4 py-3 text-sm text-danger">
              <ul className="list-disc space-y-1 pl-5">
                {generalErrors.map((error, index) => (
                  <li key={`loginpassword-general-error-${index}`}>
                    {error.message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="space-y-2">
            <label
              className="text-sm font-semibold text-ink-2"
              htmlFor="email"
            >
              {emailLabel}
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              placeholder={emailPlaceholder}
              className="input-field"
              aria-invalid={
                !!formErrors.email?.message || !!emailSDKError || undefined
              }
              {...register("email", {
                required: "Email is required",
                maxLength: {
                  value: 120,
                  message: "Maximum 120 characters allowed",
                },
              })}
            />
            {(formErrors.email?.message || emailSDKError) && (
              <p className="text-sm text-danger">
                {formErrors.email?.message || emailSDKError}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <label
                className="font-semibold text-ink-2"
                htmlFor="password"
              >
                {passwordLabel}
              </label>
              {isForgotPasswordEnabled && localizedResetLink ? (
                <a
                  href={localizedResetLink}
                  className="text-ink font-medium hover:text-ink-2"
                >
                  {forgotPasswordText}
                </a>
              ) : null}
            </div>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              placeholder={texts?.passwordPlaceholder || "********"}
              className="input-field"
              aria-invalid={
                !!formErrors.password?.message || !!passwordSDKError || undefined
              }
              {...register("password", {
                required: "Password is required",
                maxLength: {
                  value: 200,
                  message: "Maximum 200 characters allowed",
                },
              })}
            />
            {(formErrors.password?.message || passwordSDKError) && (
              <p className="text-sm text-danger">
                {formErrors.password?.message || passwordSDKError}
              </p>
            )}
          </div>

          {isCaptchaAvailable && (
            <div className="space-y-3">
              <label
                htmlFor="captcha"
                className="text-sm font-semibold text-ink-2"
              >
                {captchaLabel}
              </label>
              {captchaImage && (
                <img
                  src={captchaImage}
                  alt="CAPTCHA challenge"
                  className="h-16 w-full rounded-none border border-border object-contain"
                />
              )}
              <input
                id="captcha"
                type="text"
                placeholder={texts?.captchaCodePlaceholder || "Enter the code"}
                className="input-field"
                aria-invalid={
                  !!formErrors.captcha?.message || !!captchaSDKError || undefined
                }
                {...register("captcha", {
                  required: "Please complete the CAPTCHA",
                  maxLength: {
                    value: 15,
                    message: "CAPTCHA code is too long",
                  },
                })}
              />
              {(formErrors.captcha?.message || captchaSDKError) && (
                <p className="text-sm text-danger">
                  {formErrors.captcha?.message || captchaSDKError}
                </p>
              )}
            </div>
          )}

          {localizedEditIdentifierLink && (
            <button
              type="button"
              className="text-sm font-semibold text-ink-2 underline underline-offset-2 hover:cursor-pointer"
              onClick={() => {
                window.location.href = localizedEditIdentifierLink;
              }}
            >
              {texts?.editIdentifierText || "Use a different email"}
            </button>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded-sm border border-accent bg-accent px-4 py-3 text-base font-semibold uppercase tracking-[0.1em] text-on-accent transition hover:cursor-pointer hover:border-accent-hover hover:bg-accent-hover hover:shadow-sm disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting ? "Please wait..." : buttonText}
          </button>

          <p className="text-sm text-ink-2">
            Problems signing in?{" "}
            <a
              href="https://www.butterfield.com/my-account-faq"
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-ink-2 underline underline-offset-2 hover:cursor-pointer hover:text-ink"
            >
              Click here
            </a>
            .
          </p>

          <p className="text-sm text-ink-2">
            {signupActionText}{" "}
            {isSignupEnabled && localizedSignupLink ? (
              <a
                href={localizedSignupLink}
                className="font-semibold text-ink underline underline-offset-2"
              >
                {signupLinkText}
              </a>
            ) : (
              <span className="font-semibold text-ink">{signupLinkText}</span>
            )}
            .
          </p>
        </form>
      </div>
    </div>
  );
}

export default LoginPasswordScreen;
